import { useQueryClient } from '@tanstack/react-query'
import { Check, ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { EmptyState } from '../../components/EmptyState.tsx'
import { Obj, type ObjId } from '../../components/Obj.tsx'
import { SkCard } from '../../components/Skeleton.tsx'
import { ApiError, putPlan } from '../../lib/api.ts'
import { PlanFields } from '../onboarding/PlanPage.tsx'
import { Back } from './parts.tsx'
import { refreshPerjalanan, useRencana } from './perjalanan.ts'

/** /perjalanan/rencana — "Rencana tumbuh": the onboarding answers, editable, and the challenges and habits that fit them. */
export function RencanaPage() {
  const { data, isPending, isError, refetch } = useRencana()
  const qc = useQueryClient()
  const [berjuang, setBerjuang] = useState<string[]>([])
  const [jadi, setJadi] = useState<string | null>(null)
  const [menit, setMenit] = useState<number | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [error, setError] = useState('')

  useEffect(() => {
    if (!data || loaded) return
    setBerjuang(data.plan?.berjuang ?? [])
    setJadi(data.plan?.jadi ?? null)
    setMenit(data.plan?.menit ?? null)
    setLoaded(true)
  }, [data, loaded])

  const valid = berjuang.length > 0 && jadi !== null && menit !== null
  const edit = <T,>(set: (v: T) => void) => (v: T) => { set(v); setState('idle') }
  const save = async () => {
    setError('')
    setState('saving')
    try {
      await putPlan({ berjuang, jadi, menit })
      await refreshPerjalanan(qc)
      setState('saved')
    } catch (e) {
      setError(e instanceof ApiError && e.status !== 0 ? e.message : 'Belum tersimpan. Cek koneksi, lalu coba lagi.')
      setState('idle')
    }
  }

  return (
    <div className="page pj">
      <Back />
      <h1>Rencana tumbuh</h1>
      <p className="muted" style={{ marginTop: -6 }}>Jawabanmu dipakai untuk memilihkan tantangan dan kebiasaan yang cocok. Boleh diubah kapan saja.</p>
      {isPending && <SkCard label="Memuat rencana" lines={6} />}
      {isError && (
        <EmptyState obj="tunas" title="Belum bisa dimuat" action={<button className="btn btn-primary" onClick={() => refetch()}>Coba lagi</button>}>
          Koneksi lagi bermasalah. Cek internet kamu, lalu coba lagi.
        </EmptyState>
      )}
      {data && (
        <>
          {(data.rekomendasi.tantangan.length > 0 || data.rekomendasi.kebiasaan.length > 0) && (
            <section className="card pj-sec reveal" aria-labelledby="rc-saran-h">
              <h2 id="rc-saran-h">Cocok untukmu</h2>
              {data.rekomendasi.tantangan.length > 0 && (
                <div className="stack">
                  {data.rekomendasi.tantangan.map((p) => (
                    <Link key={p.id} className="row-card pj-inner" to={`/perjalanan/tantangan/${p.id}`}>
                      <span className="ic obj-ic" aria-hidden="true"><Obj id={p.obj as ObjId} /></span>
                      <div><b>{p.judul}</b>{' '}<small>{p.run ? (p.run.finished ? 'Sudah selesai, bisa diulang' : 'Sedang berjalan') : 'Tantangan 7 hari'}</small></div>
                      <ChevronRight className="chev" size={18} aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              )}
              {data.rekomendasi.kebiasaan.length > 0 && (
                <ul className="rc-habits">
                  {data.rekomendasi.kebiasaan.map((h) => (
                    <li key={h.key}>{h.active && <Check size={14} aria-hidden="true" />} {h.label}{h.active ? ' · sudah aktif' : ''}</li>
                  ))}
                </ul>
              )}
              {data.rekomendasi.kebiasaan.some((h) => !h.active) && <p className="help">Kebiasaan bisa diaktifkan lewat “Atur kebiasaan” di Perjalanan.</p>}
            </section>
          )}

          <PlanFields berjuang={berjuang} jadi={jadi} menit={menit} onBerjuang={edit(setBerjuang)} onJadi={edit(setJadi)} onMenit={edit(setMenit)} />
          <p className="error" role="alert">{error}</p>
          <button type="button" className="btn btn-primary btn-block" onClick={save} disabled={!valid || state === 'saving'}>
            {state === 'saving' ? 'Menyimpan…' : state === 'saved' ? 'Tersimpan' : 'Simpan rencana'}
          </button>
          {!valid && <p className="help" style={{ textAlign: 'center' }}>Jawab ketiganya dulu.</p>}
        </>
      )}
    </div>
  )
}
